// ─────────────────────────────────────────────────────────────
// CloudUploadConfirmDialog — friendly "is this OK for the cloud?"
// confirmation gate shown before an image upload.
//
// Shared by:
//   - UploadImageModal (modals.tsx) — persistent Image Library uploads
//   - PythonTab.tsx                 — ephemeral "Upload temp image" picker
//
// Informational/friendly, not a hard consent gate: Marie (first person)
// reassures the user the image is stored in a secure cloud area and asks
// them to confirm there are no IT/IP restrictions before sending it.
// "Not now" dismisses without using the file; "Yes, upload" proceeds with
// the existing flow.
// ─────────────────────────────────────────────────────────────

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { MarieIcon } from "../../components/MarieIcon";

interface CloudUploadConfirmDialogProps {
  open: boolean;
  /** Dismiss without uploading (also resets the file input upstream). */
  onCancel: () => void;
  /** Proceed with the upload. */
  onConfirm: () => void;
}

export function CloudUploadConfirmDialog({
  open,
  onCancel,
  onConfirm,
}: CloudUploadConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: "secondary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <MarieIcon fontSize={16} color="rgba(0,0,0,0.87)" />
        </Box>
        Before you upload
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          I keep your uploaded images in a secure cloud area. Before I store
          this one, please make sure it has no IT or IP restrictions and
          isn&apos;t proprietary customer data. If you&apos;re confident it
          doesn&apos;t have any of these restrictions, you&apos;re good to
          upload.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button variant="outlined" onClick={onCancel}>
          Not now
        </Button>
        <Button variant="contained" onClick={onConfirm}>
          Yes, upload
        </Button>
      </DialogActions>
    </Dialog>
  );
}
